import { styled } from '@mui/material/styles';
import { Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Chip, IconButton, Box } from '@mui/material';
import { keyframes } from '@mui/system';

// Animation keyframes
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const liquidFlow = keyframes`
  0%, 100% {
    border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  }
  25% {
    border-radius: 50% 50% 50% 50% / 50% 50% 50% 50%;
  }
  50% {
    border-radius: 70% 30% 30% 70% / 70% 70% 30% 30%;
  }
  75% {
    border-radius: 40% 60% 60% 40% / 60% 40% 40% 60%;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ripple = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
`;

// Main container with glassmorphism effect
export const LiquidPaper = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderRadius: '24px',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
  overflow: 'hidden',
  position: 'relative',
  animation: `${fadeIn} 0.6s ease-out`,

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
    backgroundSize: '200% 100%',
    animation: `${shimmer} 8s linear infinite`,
    pointerEvents: 'none',
  },

  '&:hover': {
    boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.25)',
    transform: 'translateY(-2px)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
}));

// Styled table container with smooth scrolling
export const LiquidTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '20px',
  maxHeight: '70vh',
  overflowY: 'auto',
  overflowX: 'auto',

  '&::-webkit-scrollbar': {
    width: '10px',
    height: '10px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '10px',
    border: '2px solid transparent',
    backgroundClip: 'content-box',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
    backgroundClip: 'content-box',
  },
}));

// Modern styled table
export const LiquidTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  borderCollapse: 'separate',
  borderSpacing: '0 8px',
}));

// Table header with gradient
export const LiquidTableHead = styled(TableHead)(({ theme }) => ({
  '& .MuiTableRow-root': {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '16px',

    '& .MuiTableCell-root': {
      color: '#ffffff',
      fontWeight: 600,
      fontSize: '0.95rem',
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      borderBottom: 'none',
      padding: '20px 16px',

      '&:first-of-type': {
        borderTopLeftRadius: '16px',
        borderBottomLeftRadius: '16px',
      },
      '&:last-of-type': {
        borderTopRightRadius: '16px',
        borderBottomRightRadius: '16px',
      },
    },
  },
}));

// Table body with hover effects
export const LiquidTableBody = styled(TableBody)(({ theme }) => ({
  '& .MuiTableRow-root': {
    background: 'rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(10px)',
    borderRadius: '12px',
    marginBottom: '8px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',

    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: '0',
      height: '0',
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(102, 126, 234, 0.1), transparent)',
      transition: 'width 0.6s ease, height 0.6s ease',
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'none',
    },

    '&:hover': {
      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
      transform: 'scale(1.02)',
      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.2)',

      '&::before': {
        width: '100%',
        height: '100%',
      },

      '& .MuiTableCell-root': {
        color: theme.palette.mode === 'dark' ? '#ffffff' : '#2d3436',
      },
    },

    '& .MuiTableCell-root': {
      borderBottom: 'none',
      padding: '16px',
      color: theme.palette.mode === 'dark' ? '#e0e0e0' : '#636e72',
      fontSize: '0.9rem',

      '&:first-of-type': {
        borderTopLeftRadius: '12px',
        borderBottomLeftRadius: '12px',
      },
      '&:last-of-type': {
        borderTopRightRadius: '12px',
        borderBottomRightRadius: '12px',
      },
    },
  },
}));

// Modern chip with liquid effect
export const LiquidChip = styled(Chip)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#ffffff',
  fontWeight: 500,
  borderRadius: '20px',
  padding: '4px 8px',
  border: 'none',
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.2)',
    transform: 'translate(-50%, -50%) scale(0)',
    borderRadius: '50%',
    transition: 'transform 0.5s ease',
  },

  '&:hover::before': {
    transform: 'translate(-50%, -50%) scale(2)',
  },

  '&.status-active': {
    background: 'linear-gradient(135deg, #00d2d3 0%, #00a8cc 100%)',
  },
  '&.status-pending': {
    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  },
  '&.status-expired': {
    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  },
  '&.status-success': {
    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  },
}));

// Modern icon button with ripple effect
export const LiquidIconButton = styled(IconButton)(({ theme }) => ({
  position: 'relative',
  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
  backdropFilter: 'blur(10px)',
  borderRadius: '12px',
  padding: '8px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '0',
    height: '0',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(102, 126, 234, 0.4), transparent)',
    transform: 'translate(-50%, -50%)',
    transition: 'width 0.3s ease, height 0.3s ease',
  },

  '&:hover': {
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
    transform: 'scale(1.1)',

    '&::before': {
      width: '100%',
      height: '100%',
    },
  },

  '&:active': {
    transform: 'scale(0.95)',
  },

  '& .MuiSvgIcon-root': {
    color: theme.palette.mode === 'dark' ? '#e0e0e0' : '#636e72',
    transition: 'color 0.3s ease',
  },

  '&:hover .MuiSvgIcon-root': {
    color: '#667eea',
  },
}));

// Table toolbar with gradient
export const LiquidTableToolbar = styled(Box)(({ theme }) => ({
  padding: '20px',
  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
  borderRadius: '20px 20px 0 0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',

  '& .toolbar-title': {
    fontSize: '1.2rem',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },

  '& .toolbar-actions': {
    display: 'flex',
    gap: '12px',
  },
}));

// Loading skeleton with liquid animation
export const LiquidSkeleton = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '60px',
  borderRadius: '12px',
  background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.05) 25%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 75%)',
  backgroundSize: '200% 100%',
  animation: `${shimmer} 2s linear infinite`,
  marginBottom: '8px',
}));

// Liquid background decoration
export const LiquidBackground = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '-50%',
  right: '-20%',
  width: '400px',
  height: '400px',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '50%',
  filter: 'blur(100px)',
  opacity: 0.2,
  animation: `${liquidFlow} 10s ease-in-out infinite`,
  pointerEvents: 'none',
  zIndex: -1,
}));

// Empty state with liquid animation
export const LiquidEmptyState = styled(Box)(({ theme }) => ({
  padding: '60px 40px',
  textAlign: 'center',
  position: 'relative',

  '& .empty-icon': {
    width: '120px',
    height: '120px',
    margin: '0 auto 24px',
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: `${liquidFlow} 8s ease-in-out infinite`,

    '& svg': {
      fontSize: '48px',
      color: '#667eea',
    },
  },

  '& .empty-title': {
    fontSize: '1.4rem',
    fontWeight: 600,
    color: theme.palette.mode === 'dark' ? '#e0e0e0' : '#2d3436',
    marginBottom: '12px',
  },

  '& .empty-description': {
    fontSize: '1rem',
    color: theme.palette.mode === 'dark' ? '#b0b0b0' : '#636e72',
  },
}));